import React, { useEffect, useRef } from "react";
import { MapPin, Phone } from "lucide-react";
import { TOWNS, SITE } from "../site.config";

const DURHAM_LAT = 54.7753;
const DURHAM_LON = -1.5849;
const COVERAGE_RADIUS_M = 48280; // ~30 miles in metres

export default function Coverage() {
  const mapElRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    // Wait for global L (Leaflet) to be available
    let attempts = 0;
    const id = setInterval(() => {
      attempts += 1;
      if (window.L && mapElRef.current && !mapInstanceRef.current) {
        clearInterval(id);
        const L = window.L;
        const map = L.map(mapElRef.current, {
          center: [DURHAM_LAT, DURHAM_LON],
          zoom: 9,
          scrollWheelZoom: false, // require explicit gesture (cmd/ctrl + scroll)
          zoomControl: true,
        });
        mapInstanceRef.current = map;

        L.tileLayer(
          "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          {
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            maxZoom: 18,
          },
        ).addTo(map);

        // Coverage circle (geo-anchored)
        L.circle([DURHAM_LAT, DURHAM_LON], {
          radius: COVERAGE_RADIUS_M,
          color: "#e51c24",
          weight: 3,
          dashArray: "8 6",
          fillColor: "#e51c24",
          fillOpacity: 0.07,
          interactive: false,
        }).addTo(map);

        // Base marker
        const icon = L.divIcon({
          className: "moffat-base-pin",
          html:
            '<div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-100%);">' +
            '<div style="background:#e51c24;color:#fff;font-family:\'Archivo Black\',sans-serif;font-size:11px;letter-spacing:.18em;padding:3px 8px;border-radius:3px;box-shadow:0 2px 6px rgba(0,0,0,0.3);white-space:nowrap;">DURHAM</div>' +
            '<div style="width:14px;height:14px;background:#e51c24;border-radius:999px;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.35);margin-top:3px;"></div>' +
            "</div>",
          iconSize: [80, 36],
          iconAnchor: [40, 36],
        });
        L.marker([DURHAM_LAT, DURHAM_LON], { icon, interactive: false }).addTo(
          map,
        );

        // Enable scroll zoom once user clicks the map (avoids hijacking page scroll)
        map.on("click", () => map.scrollWheelZoom.enable());
        map.on("mouseout", () => map.scrollWheelZoom.disable());
      } else if (attempts > 40) {
        clearInterval(id);
      }
    }, 150);

    return () => {
      clearInterval(id);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <section
      id="coverage"
      data-testid="coverage-section"
      className="section-pad bg-moffat-navy text-white relative overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, #ffffff 1.5px, transparent 1.5px), radial-gradient(circle at 70% 60%, #ffffff 1.5px, transparent 1.5px)",
          backgroundSize: "44px 44px, 64px 64px",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="font-script text-3xl text-moffat-red leading-none">
              Areas we cover
            </p>
            <h2 className="font-display text-4xl sm:text-5xl mt-3 uppercase leading-[0.95]">
              Proudly serving
              <br />
              <span className="text-moffat-red">County Durham</span>
              <br />
              &amp; the North East.
            </h2>
            <p className="mt-5 text-white/75 text-base leading-relaxed max-w-md">
              Based in County Durham — we cover roughly a{" "}
              <strong className="text-white">30 mile radius</strong> across the
              North East for homes, landlords and businesses. Drag and zoom the
              map to explore.
            </p>
            <a
              data-testid="coverage-cta"
              href={`tel:${SITE.phoneTel}`}
              className="mt-7 inline-flex items-center gap-2 bg-moffat-red hover:bg-moffat-redDeep text-white font-bold px-6 py-3.5 rounded-sm transition-all hover:-translate-y-0.5"
            >
              <Phone size={16} strokeWidth={2.8} />
              CALL US TODAY
            </a>
          </div>

          <div className="lg:col-span-7 space-y-5">
            {/* Interactive map card */}
            <div
              data-testid="coverage-map"
              className="relative rounded-sm overflow-hidden border-2 border-white/15 shadow-2xl bg-white"
            >
              <div
                ref={mapElRef}
                className="relative w-full aspect-[16/10] z-0"
                aria-label="Interactive coverage map — drag and zoom"
              />

              {/* Legend */}
              <div className="bg-white text-moffat-navy px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-3 w-3 rounded-full bg-moffat-red border-2 border-white shadow-sm" />
                  <span className="font-bold">Base — Durham</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-3 w-5 border-t-2 border-dashed"
                    style={{ borderColor: "rgba(229,28,36,0.85)" }}
                  />
                  <span className="font-semibold text-moffat-muted">
                    ~30 mile radius
                  </span>
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-moffat-muted">
                  Drag · pinch to zoom
                </span>
              </div>
            </div>

            {/* Towns grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {TOWNS.map((t) => (
                <div
                  key={t}
                  data-testid={`coverage-town-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group flex items-center gap-2.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-moffat-red rounded-sm px-3.5 py-3 transition-all"
                >
                  <MapPin
                    size={14}
                    className="text-moffat-red flex-shrink-0"
                    strokeWidth={2.8}
                  />
                  <span className="text-sm font-semibold">{t}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 bg-moffat-red text-white font-display rounded-sm px-3.5 py-3 text-sm">
                + MANY MORE
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
